'use client';

import { Loader2, Plug, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmationDialog } from '../../molecules/confirmation-dialog/confirmation-dialog';

export type DiscoveryStep = 'plants' | 'meters';

export interface ProviderFieldDef {
	/** Chiave del campo nell'oggetto config. */
	key: string;
	/** Etichetta del campo. */
	label: string;
	/** Tipo di input (testo o password mascherata). */
	type: 'text' | 'password';
	/** Testo segnaposto. */
	placeholder?: string;
	/** Se true, il campo è obbligatorio per salvare/testare la connessione. */
	required: boolean;
	/** Nota esplicativa sotto il campo. */
	help?: string;
	/** Se valorizzato, il campo è popolabile via discovery con questo step. */
	discoverable?: DiscoveryStep;
}

export interface ProviderDefinition {
	/** Id univoco del provider. */
	id: string;
	/** Nome visualizzato del provider. */
	label: string;
	/** Campi di configurazione richiesti dal provider. */
	configFields: ProviderFieldDef[];
}

export interface ConnectionDTO {
	/** Id del POD collegato. */
	podId: string;
	/** Id del provider configurato. */
	provider: string;
	/** Configurazione salvata (credenziali, impianto/contatore selezionati). */
	config: Record<string, string>;
	/** Se true, la raccolta dati è attiva. */
	enabled: boolean;
	/** Stato corrente della connessione. */
	status: 'pending' | 'active' | 'error';
	/** Data/ora dell'ultima sincronizzazione riuscita (ISO). */
	lastSyncAt: string | null;
	/** Messaggio dell'ultimo errore di sincronizzazione. */
	lastError: string | null;
}

export interface DiscoveryOption {
	/** Id dell'elemento scoperto (impianto o contatore). */
	id: string;
	/** Nome visualizzato dell'elemento scoperto. */
	label: string;
}

export interface SyncResult {
	/** Se true, la sincronizzazione è riuscita. */
	ok: boolean;
	/** Numero di letture inserite. */
	inserted: number;
	/** Messaggio descrittivo dell'esito. */
	message: string;
}

export interface PodApiConnectionPanelProps {
	/** Provider disponibili per la connessione. */
	providers: ProviderDefinition[];
	/** Provider mostrati come "in arrivo" (disabilitati nel select). */
	comingSoonProviders: { id: string; label: string }[];
	/** Carica la connessione già configurata per il POD, se presente. */
	onLoad: () => Promise<ConnectionDTO | null>;
	/** Cerca impianti o contatori disponibili per il provider/config correnti. */
	onDiscover: (
		step: DiscoveryStep,
		provider: string,
		config: Record<string, string>,
	) => Promise<DiscoveryOption[]>;
	/** Testa la connessione con la configurazione corrente, senza salvarla. */
	onTest: (
		provider: string,
		config: Record<string, string>,
	) => Promise<{ ok: boolean; message: string }>;
	/** Salva la configurazione della connessione. */
	onSave: (provider: string, config: Record<string, string>) => Promise<ConnectionDTO>;
	/** Avvia una sincronizzazione immediata. */
	onSync: () => Promise<SyncResult>;
	/** Rimuove la connessione configurata. */
	onRemove: () => Promise<void>;
}

const INPUT_CLASS =
	'w-full rounded-[7px] border border-border bg-background px-2.5 py-1.5 text-[13px] text-text-primary outline-none transition-colors focus:border-accent';

const STATUS_BADGE: Record<ConnectionDTO['status'], { label: string; className: string }> = {
	active: {
		label: 'Attiva',
		className: 'border-accent-green/30 bg-accent-green/10 text-accent-green',
	},
	pending: { label: 'In attesa di sync', className: 'border-warn/30 bg-warn/10 text-warn' },
	error: { label: 'Errore', className: 'border-danger/30 bg-danger/10 text-danger' },
};

/**
 * Configurazione della connessione API del misuratore per un POD:
 * provider (dropdown estensibile), credenziali, test e sync immediato.
 */
export function PodApiConnectionPanel({
	providers,
	comingSoonProviders,
	onLoad,
	onDiscover,
	onTest,
	onSave,
	onSync,
	onRemove,
}: PodApiConnectionPanelProps) {
	const [loading, setLoading] = useState(true);
	const [connection, setConnection] = useState<ConnectionDTO | null>(null);
	const [providerId, setProviderId] = useState<string>(providers[0]?.id ?? '');
	const [config, setConfig] = useState<Record<string, string>>({});
	const [testing, setTesting] = useState(false);
	const [saving, setSaving] = useState(false);
	const [syncing, setSyncing] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [confirmRemove, setConfirmRemove] = useState(false);
	const [options, setOptions] = useState<Partial<Record<DiscoveryStep, DiscoveryOption[]>>>({});
	const [discovering, setDiscovering] = useState<DiscoveryStep | null>(null);

	const definition = providers.find((d) => d.id === providerId);

	const discover = useCallback(
		async (step: DiscoveryStep, provider: string, cfg: Record<string, string>) => {
			if (!cfg.apiKey?.trim()) return;
			if (step === 'meters' && !cfg.plantId?.trim()) return;
			setDiscovering(step);
			try {
				const opts = await onDiscover(step, provider, cfg);
				setOptions((prev) => ({ ...prev, [step]: opts }));
			} catch (err) {
				toast.error(err instanceof Error ? err.message : 'Discovery fallita');
			} finally {
				setDiscovering(null);
			}
		},
		[onDiscover],
	);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const conn = await onLoad();
			setConnection(conn);
			if (conn) {
				setProviderId(conn.provider);
				setConfig(conn.config);
				// popola i dropdown con i dati già configurati (la apiKey mascherata
				// viene risolta lato server dalla connessione salvata)
				discover('plants', conn.provider, conn.config);
				discover('meters', conn.provider, conn.config);
			}
		} catch {
			// pannello secondario: in caso di errore lasciamo il form vuoto
		} finally {
			setLoading(false);
		}
	}, [onLoad, discover]);

	useEffect(() => {
		load();
	}, [load]);

	async function handleTest() {
		setTesting(true);
		try {
			const res = await onTest(providerId, config);
			if (res.ok) toast.success(res.message);
			else toast.error(res.message);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Test fallito');
		} finally {
			setTesting(false);
		}
	}

	async function handleSave() {
		setSaving(true);
		try {
			const saved = await onSave(providerId, config);
			setConnection(saved);
			setConfig(saved.config);
			toast.success('Connessione API salvata - avvio prima raccolta…');
			// prima raccolta immediata: innesca lo stato del collector
			const sync = await onSync();
			if (sync.ok) toast.success(sync.message);
			else toast.error(`Prima raccolta fallita: ${sync.message}`);
			await load();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore nel salvataggio');
		} finally {
			setSaving(false);
		}
	}

	async function handleSync() {
		setSyncing(true);
		try {
			const res = await onSync();
			if (res.ok) toast.success(res.message);
			else toast.error(res.message);
			await load();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore di sincronizzazione');
		} finally {
			setSyncing(false);
		}
	}

	async function handleRemove() {
		setRemoving(true);
		try {
			await onRemove();
			setConnection(null);
			setConfig({});
			toast.success('Connessione API rimossa');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Errore nella rimozione');
		} finally {
			setRemoving(false);
		}
	}

	function renderFieldInput(field: ProviderFieldDef) {
		if (!field.discoverable) {
			return (
				<input
					type={field.type}
					value={config[field.key] ?? ''}
					onChange={(e) => setConfig((c) => ({ ...c, [field.key]: e.target.value }))}
					onBlur={() => {
						// inserita la apiKey → popola il dropdown impianti
						if (field.key === 'apiKey' && config[field.key]?.trim()) {
							discover('plants', providerId, config);
						}
					}}
					className={INPUT_CLASS}
					placeholder={field.placeholder}
					autoComplete="off"
				/>
			);
		}

		const step = field.discoverable;
		const list = options[step];
		const value = config[field.key] ?? '';
		const isDiscovering = discovering === step;
		const canDiscover =
			Boolean(config.apiKey?.trim()) && (step !== 'meters' || Boolean(config.plantId?.trim()));

		const onSelect = (newValue: string) => {
			const next = { ...config, [field.key]: newValue };
			if (step === 'plants') {
				// cambio impianto → il contatore selezionato non vale più
				next.meterId = '';
				setOptions((prev) => ({ ...prev, meters: undefined }));
			}
			setConfig(next);
			if (step === 'plants' && newValue) discover('meters', providerId, next);
		};

		return (
			<div className="flex items-center gap-1.5">
				{list ? (
					<select value={value} onChange={(e) => onSelect(e.target.value)} className={INPUT_CLASS}>
						<option value="">Seleziona…</option>
						{value && !list.some((o) => o.id === value) && <option value={value}>{value}</option>}
						{list.map((o) => (
							<option key={o.id} value={o.id}>
								{o.label}
							</option>
						))}
					</select>
				) : (
					<input
						type="text"
						value={value}
						onChange={(e) => setConfig((c) => ({ ...c, [field.key]: e.target.value }))}
						className={INPUT_CLASS}
						placeholder={field.placeholder}
						autoComplete="off"
					/>
				)}
				<button
					type="button"
					onClick={() => discover(step, providerId, config)}
					disabled={!canDiscover || isDiscovering}
					title={
						canDiscover
							? `Cerca ${step === 'plants' ? 'impianti' : 'contatori'}`
							: step === 'meters'
								? 'Inserisci prima eu-apikey e impianto'
								: 'Inserisci prima la eu-apikey'
					}
					className="rounded border border-border p-1.5 text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-40"
				>
					{isDiscovering ? (
						<Loader2 className="h-3 w-3 animate-spin" />
					) : (
						<Search className="h-3 w-3" />
					)}
				</button>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="flex h-8 items-center gap-2 text-[13px] text-text-muted">
				<Loader2 className="h-3 w-3 animate-spin" /> Caricamento connessione API...
			</div>
		);
	}

	return (
		<div className="space-y-2 rounded-lg border border-border/70 bg-background/50 p-3">
			<ConfirmationDialog
				open={confirmRemove}
				onOpenChange={setConfirmRemove}
				title="Rimuovere la connessione API?"
				description="La raccolta dati per questo POD si fermerà."
				confirmText="Rimuovi"
				isLoading={removing}
				onConfirm={handleRemove}
			/>
			<div className="flex items-center gap-2">
				<Plug className="h-3 w-3 text-accent" />
				<span className="text-[11px] font-bold uppercase tracking-widest text-accent">
					Connessione API misuratore
				</span>
				{connection && (
					<span
						className={`ml-auto rounded-full border px-2 py-0.5 text-[11px] ${STATUS_BADGE[connection.status].className}`}
					>
						{STATUS_BADGE[connection.status].label}
					</span>
				)}
			</div>

			{connection?.lastSyncAt && (
				<p className="text-[11px] text-text-muted">
					Ultima sincronizzazione: {new Date(connection.lastSyncAt).toLocaleString('it-IT')}
				</p>
			)}
			{connection?.status === 'error' && connection.lastError && (
				<p className="rounded border border-danger/20 bg-danger/5 px-2 py-1 text-[11px] text-danger">
					{connection.lastError}
				</p>
			)}

			<label className="block">
				<span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
					Provider
				</span>
				<select
					value={providerId}
					onChange={(e) => {
						setProviderId(e.target.value);
						setConfig({});
						setOptions({});
					}}
					className={INPUT_CLASS}
				>
					{providers.map((d) => (
						<option key={d.id} value={d.id}>
							{d.label}
						</option>
					))}
					{comingSoonProviders.map((p) => (
						<option key={p.id} value={p.id} disabled>
							{p.label} - in arrivo
						</option>
					))}
				</select>
			</label>

			{definition?.configFields.map((field) => (
				<label key={field.key} className="block">
					<span className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
						{field.label}
						{field.required && ' *'}
					</span>
					{renderFieldInput(field)}
					{field.help && <span className="text-[10px] italic text-text-muted">{field.help}</span>}
				</label>
			))}

			<div className="flex flex-wrap items-center gap-2 pt-1">
				<button
					type="button"
					onClick={handleTest}
					disabled={testing || saving}
					className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-[12px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
				>
					{testing && <Loader2 className="h-3 w-3 animate-spin" />}
					Testa connessione
				</button>
				<button
					type="button"
					onClick={handleSave}
					disabled={saving || testing}
					className="inline-flex items-center gap-1 rounded border border-accent bg-accent/10 px-3 py-1 text-[12px] font-semibold text-accent disabled:opacity-50"
				>
					{saving && <Loader2 className="h-3 w-3 animate-spin" />}
					{connection ? 'Aggiorna e sincronizza' : 'Attiva raccolta dati'}
				</button>
				{connection && (
					<>
						<button
							type="button"
							onClick={handleSync}
							disabled={syncing}
							className="inline-flex items-center gap-1 rounded border border-border px-3 py-1 text-[12px] text-text-muted transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
						>
							{syncing ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<RefreshCw className="h-3 w-3" />
							)}
							Sincronizza ora
						</button>
						<button
							type="button"
							onClick={() => setConfirmRemove(true)}
							disabled={removing}
							className="inline-flex items-center gap-1 rounded border border-danger/30 px-2 py-1 text-[12px] text-danger transition-colors hover:bg-danger/10 disabled:opacity-50"
						>
							{removing ? (
								<Loader2 className="h-3 w-3 animate-spin" />
							) : (
								<Trash2 className="h-3 w-3" />
							)}
							Rimuovi
						</button>
					</>
				)}
			</div>
		</div>
	);
}
